import React, { useState, useEffect } from 'react';
import { SelectChangeEvent } from '@mui/material';
import apiAxiosInstance from '../../../../utils/axiosInstance';
import { extractTeachingStandardId } from '../../../../utils/mongodb';
import { useTeacherStandard } from '../../../../hooks/useTeacherStandard';
import { useFilterStandardsByTeaching } from '../../../../hooks/useFilterStandardsByTeaching';
import type { Teacher } from '../../../../Types';
import type { Standard } from '../assessmentsTypes';

export interface UseAssessmentFormOptions {
  teacher: Teacher | null;
  setError: (message: string | null) => void;
}

export function useAssessmentForm({ teacher, setError }: UseAssessmentFormOptions) {
  const [topic, setTopic] = useState('');
  const [genre, setGenre] = useState('Informational');
  const [questionStyle, setQuestionStyle] = useState('StandardizedTest');
  const [showAnswerKey, setShowAnswerKey] = useState(true);
  const [isPairedPassage, setIsPairedPassage] = useState(false);
  const [genreTwo, setGenreTwo] = useState('');
  const [revisingAndEditing, setRevisingAndEditing] = useState(false);
  const [language, setLanguage] = useState<'en' | 'es'>('en');
  const [gradeLevel, setGradeLevel] = useState('');
  const [standards, setStandards] = useState<Standard[]>([]);
  const [selectedStandards, setSelectedStandards] = useState<string[]>([]);
  const [availableGradeLevels, setAvailableGradeLevels] = useState<string[]>([]);
  const [loadingGradeLevels, setLoadingGradeLevels] = useState(false);
  const [standardsMenuOpen, setStandardsMenuOpen] = useState(false);
  
  // Strand filter state - initialize to 'Reading' if available, otherwise first available strand
  const [selectedStrand, setSelectedStrand] = useState<string>('Reading');
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);

  // Add paragraph count state
  const [paragraphCount, setParagraphCount] = useState<number>(4);
  
  // Add question mix state for dynamic question types
  const [questionMix, setQuestionMix] = useState<string>('multiple_choice'); // 'multiple_choice', 'mixed', 'text_only'
  
  // Add state for generating questions
  const [generateQuestions, setGenerateQuestions] = useState<boolean>(true);

  // Add state for image generation
  const [generateImages, setGenerateImages] = useState<boolean>(false);
  const [imageCount, setImageCount] = useState<number>(1);

  const getDefaultParagraphCount = (grade: string): number => {
    switch (grade) {
      case 'K':
      case '1':
        return 2; // Very short passages for early grades
      case '2':
        return 3; // Short passages for grade 2
      case '3':
        return 4; // Standard length for grade 3
      case '4':
        return 4; // Standard length for grade 4
      case '5':
        return 5; // Slightly longer for grade 5
      case '6':
        return 5; // Standard length for grade 6
      case '7':
        return 6; // Longer passages for grade 7
      case '8':
        return 6; // Longer passages for grade 8
      case '9':
      case '10':
        return 7; // Longer passages for high school
      case '11':
      case '12':
      case 'Reading':
      case 'Speech':
        return 8; // Extended passages for upper high school
      default:
        return 4; // Default fallback
    }
  };

  // Helper function to map teacher primarySubject to API subject parameter
  const mapSubjectToAPIParam = (primarySubject?: string): string | undefined => {
    if (!primarySubject) return undefined;
    
    const subjectMap: Record<string, string> = {
      'ELA': 'ELA',
      'Reading': 'ELA',
      'Math': 'Math',
      'Mathematics': 'Math',
      'Science': 'Science',
      'Social Studies': 'Social Studies'
    };
    
    return subjectMap[primarySubject] || primarySubject;
  };

  // Use custom hook to get teacher's standard name
  const { standardName: teachingStandardName, loading: standardLoading, error: standardError } = useTeacherStandard(teacher);
  
  // Helper function to determine actual text style for API calls based on teacher's teaching standard
  const getActualTextStyle = (selectedStyle: string): string => {
    if (selectedStyle === 'ReadingComprehension') {
      return 'Generic';
    }
    // StandardizedTest - detect based on teaching standard
    if (!teachingStandardName) return 'STAAR'; // Default fallback
    const upperName = teachingStandardName.toUpperCase();
    if (upperName.includes('TEKS') || upperName.includes('TEXAS')) {
      return 'STAAR';
    }
    if (upperName.includes('COMMON CORE') || upperName.includes('CCSS')) {
      return 'SBAC';
    }
    return 'STAAR'; // Default fallback
  };
  
  // Filter standards by teacher's teaching standard
  const filteredStandards = useFilterStandardsByTeaching(standards, teachingStandardName, 'Assessments');
  
  // Get available strands from filtered standards
  const availableStrands = React.useMemo(() => {
    const strandsSet = new Set<string>();
    filteredStandards.forEach(standard => {
      if (standard.strand) {
        strandsSet.add(standard.strand);
      }
    });
    const strandsArray = Array.from(strandsSet).sort();
    
    // Capitalize first letter of each strand for display
    const capitalizedStrands = strandsArray.map(strand => {
      return strand.charAt(0).toUpperCase() + strand.slice(1).toLowerCase();
    });
    
    // Find Reading (case-insensitive) and ensure it's first
    const readingIndex = capitalizedStrands.findIndex(s => s.toLowerCase() === 'reading');
    if (readingIndex !== -1) {
      const reading = capitalizedStrands[readingIndex];
      return [reading, ...capitalizedStrands.filter(s => s !== reading)];
    }
    return capitalizedStrands;
  }, [filteredStandards]);
  
  // Map of capitalized display names to original DB values
  const strandDisplayToOriginal = React.useMemo(() => {
    const map = new Map<string, string>();
    filteredStandards.forEach(standard => {
      if (standard.strand) {
        const capitalized = standard.strand.charAt(0).toUpperCase() + standard.strand.slice(1).toLowerCase();
        if (!map.has(capitalized)) {
          map.set(capitalized, standard.strand);
        }
      }
    });
    return map;
  }, [filteredStandards]);
  
  // Filter standards by selected strand
  const strandFilteredStandards = React.useMemo(() => {
    if (!selectedStrand || selectedStrand === 'All') {
      return filteredStandards;
    }
    // Get original strand value from display name
    const originalStrand = strandDisplayToOriginal.get(selectedStrand) || selectedStrand;
    return filteredStandards.filter(standard => standard.strand === originalStrand);
  }, [filteredStandards, selectedStrand, strandDisplayToOriginal]);
  
  // Update selectedStrand when availableStrands changes to ensure Reading is selected if available
  React.useEffect(() => {
    if (availableStrands.length > 0) {
      const readingStrand = availableStrands.find(s => s.toLowerCase() === 'reading');
      // If current selection is not in available strands, or it's the default 'Reading' value
      if (!availableStrands.includes(selectedStrand)) {
        // Select Reading if available, otherwise "All Strands"
        setSelectedStrand(readingStrand || 'All');
      } else if (readingStrand && selectedStrand === 'Reading') {
        // If we have the default 'Reading' string but the actual value is different, update it
        if (selectedStrand !== readingStrand) {
          setSelectedStrand(readingStrand);
        }
      }
    }
  }, [availableStrands]); // Only depend on availableStrands to avoid infinite loop

  // Fetch available grade levels from database based on subject and teaching standards
  useEffect(() => {
    const fetchGradeLevels = async () => {
      if (!teacher?.teachingStandards || !Array.isArray(teacher.teachingStandards) || teacher.teachingStandards.length === 0) {
        setAvailableGradeLevels([]);
        return;
      }

      // Extract the first teaching standard ID (handle both string and object formats)
      const teachingStandardsId = extractTeachingStandardId(teacher.teachingStandards);

      if (!teachingStandardsId) {
        setAvailableGradeLevels([]);
        return;
      }

      setLoadingGradeLevels(true);
      try {
        const subjectParam = mapSubjectToAPIParam(teacher.primarySubject);
        const params: any = { 
          teachingStandards: teachingStandardsId
        };
        if (subjectParam) {
          params.subject = subjectParam;
        }
        const res = await apiAxiosInstance.get('/api/detailedstandards/grade-levels', { params });
        setAvailableGradeLevels(res.data || []);
      } catch (e) {
        console.error('Error fetching grade levels:', e);
        setAvailableGradeLevels([]);
      } finally {
        setLoadingGradeLevels(false);
      }
    };

    fetchGradeLevels();
  }, [teacher?.teachingStandards, teacher?.primarySubject]);

  // Helper function to capitalize the first letter of a string
  const capitalizeFirstLetter = (str: string | undefined | null): string => {
    if (!str || str.length === 0) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Fetch standards once teacher is known
  useEffect(() => {
    if (teacher?.gradeLevel) {
      // Handle numeric grades (K, 1-12) and special levels (Reading, Speech)
      let grade = teacher.gradeLevel;
      if (/^\d+$/.test(grade) || grade === 'K') {
        // Already a valid grade level
      } else if (/grade\s*(\d+|k)/i.test(grade)) {
        // Extract grade number from "Grade X" format
        const match = grade.match(/grade\s*(\d+|k)/i);
        grade = match ? match[1].toUpperCase() : grade;
      }
      setGradeLevel(grade);
      
      // Auto-set paragraph count based on grade level
      const defaultParagraphs = getDefaultParagraphCount(grade);
      setParagraphCount(defaultParagraphs);
      
      const fetchStandards = async () => {
        try {
          // Build API params with subject filter from teacher profile
          const apiParams: { gradeLevel: string; subject?: string } = {
            gradeLevel: grade
          };
          
          // Add subject filter if teacher has primarySubject set
          const subjectParam = mapSubjectToAPIParam(teacher.primarySubject);
          if (subjectParam) {
            apiParams.subject = subjectParam;
          }
          
          console.log('📚 Fetching standards with params:', apiParams);
          console.log('👤 Teacher profile:', {
            primarySubject: teacher.primarySubject,
            teachingStandards: teacher.teachingStandards,
            subjectPreferences: teacher.subjectPreferences
          });
          
          const response = await apiAxiosInstance.get('/api/detailedstandards', {
            params: apiParams
          });
          
          console.log(`📊 Received ${response.data.length} standards from API`);
          
          setStandards(response.data);
        } catch (error: any) {
          console.error('Error fetching standards:', error);
          setError('Failed to fetch standards');
        }
      };
      
      fetchStandards();
    }
  }, [teacher?.gradeLevel, teacher?.primarySubject, teacher?.teachingStandards, teachingStandardName]);

  const handleGradeLevelChange = async (event: SelectChangeEvent) => {
    const newGradeLevel = event.target.value;
    setGradeLevel(newGradeLevel);
    setSelectedStandards([]);
    
    // Auto-set paragraph count based on grade level
    const defaultParagraphs = getDefaultParagraphCount(newGradeLevel);
    setParagraphCount(defaultParagraphs);
    
    try {
      // Build API params with subject filter from teacher profile
      const apiParams: { gradeLevel: string; subject?: string } = {
        gradeLevel: newGradeLevel
      };
      
      // Add subject filter if teacher has primarySubject set
      const subjectParam = mapSubjectToAPIParam(teacher?.primarySubject);
      if (subjectParam) {
        apiParams.subject = subjectParam;
      }
      
      console.log('📚 Grade change - Fetching standards with params:', apiParams);
      
      const response = await apiAxiosInstance.get('/api/detailedstandards', {
        params: apiParams
      });
      
      console.log(`📊 Received ${response.data.length} standards from API`);
      
      setStandards(response.data);
    } catch (error: any) {
      console.error('Error fetching standards:', error);
      setError('Failed to fetch standards');
    }
  };
  return {
    topic, setTopic, genre, setGenre, questionStyle, setQuestionStyle, showAnswerKey,
    isPairedPassage, setIsPairedPassage, genreTwo, setGenreTwo,
    revisingAndEditing, setRevisingAndEditing, language, setLanguage,
    gradeLevel, standards, selectedStandards, setSelectedStandards,
    availableGradeLevels, loadingGradeLevels, selectedStrand, setSelectedStrand,
    isFormCollapsed, setIsFormCollapsed, paragraphCount, setParagraphCount,
    questionMix, setQuestionMix, generateQuestions, setGenerateQuestions,
    getDefaultParagraphCount, getActualTextStyle, availableStrands, strandFilteredStandards,
    standardLoading, capitalizeFirstLetter, handleGradeLevelChange,
    generateImages, setGenerateImages, imageCount, setImageCount,
  };
}
